/**
 * Generic record table — renders any object in fin.meta_objects.
 *
 * No per-object code: the catalogue supplies the columns, their types, and
 * which of them are editable. Adding a table to the interface means adding
 * catalogue rows.
 *
 * Column mechanics — visibility, resizing, sort order — come from TanStack
 * Table rather than being hand-rolled. Sorting and search run on the SERVER:
 * filtering only the loaded page is misleading on a 16,000-row object, and
 * sorting it is simply wrong.
 *
 * Edits update the row in place from the PATCH response. The previous version
 * refetched the page, which was both the lag and the reason an edited row
 * jumped position.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  flexRender, getCoreRowModel, useReactTable,
  type ColumnDef, type ColumnSizingState, type VisibilityState,
} from '@tanstack/react-table';
import { toast } from 'sonner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Search, ChevronLeft, ChevronRight, Lock, Sparkles, Plus, PanelRight,
  SlidersHorizontal, ArrowUp, ArrowDown, ChevronsUpDown, X,
} from 'lucide-react';
import { EditableCell, type Attribute, type Override } from './EditableCell';
import {
  FilterBar, usableFilters,
  type FilterClause, type FilterableAttribute, type OperatorDef,
} from './FilterBar';
import { RecordSheet } from './RecordSheet';

interface RecordRow {
  __key: string;
  __overrides?: Record<string, Override> | null;
  [k: string]: unknown;
}

interface Payload {
  object: { slug: string; label: string; mode: 'editable' | 'overlay' | 'readonly' };
  source_system: string | null;
  attributes: FilterableAttribute[];
  /** The conditions the server can honour, and which need a value. */
  filter_operators: OperatorDef[];
  total: number;
  records: RecordRow[];
}

const PAGE = 50;

export default function RecordsTable({ object }: { object: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Search and sort go to the server — see the note at the top of the file.
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sort, setSort] = useState<{ id: string; desc: boolean } | null>(null);
  // Filters go to the server too. A half-written clause stays in state so the
  // chip keeps its place while it is being filled in, but is not sent.
  const [filters, setFilters] = useState<FilterClause[]>([]);

  const [visibility, setVisibility] = useState<VisibilityState>({});
  const [sizing, setSizing] = useState<ColumnSizingState>({});
  // Column order is the user's, not the catalogue's. Empty means "as catalogued".
  const [order, setOrder] = useState<string[]>([]);
  // The drag is tracked in a ref as well as state: state drives the highlight,
  // but a drop must not depend on a re-render having landed between two mouse
  // events. The dataTransfer payload is the real source of truth.
  const dragRef = useRef<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetRecord, setSheetRecord] = useState<RecordRow | null>(null);

  // Labels for the reference values on the current page, so cells read as
  // names rather than keys. Only the values on screen are resolved — asking
  // for the whole target object would be capped, and silently wrong past the
  // cap on any object with more than 500 rows.
  const [lookups, setLookups] = useState<Record<string, { value: string; label: string }[]>>({});

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebounced(search); setOffset(0); }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  // Serialised, so the fetch re-runs when a clause actually changes rather
  // than on every keystroke that rebuilds the array identity.
  const activeFilterKey = JSON.stringify(usableFilters(filters));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
      if (debounced) p.set('q', debounced);
      if (sort) { p.set('sort', sort.id); p.set('dir', sort.desc ? 'desc' : 'asc'); }
      if (activeFilterKey !== '[]') p.set('filters', activeFilterKey);
      const res = await fetch(`/api/fin/records/${object}?${p}`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Could not load ${object}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load records');
    } finally {
      setLoading(false);
    }
  }, [object, offset, debounced, sort, activeFilterKey]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    const refs = data.attributes.filter((a) => a.type === 'reference' && a.references_object);
    if (!refs.length) return;

    // Group the values on screen by the object they point at.
    const wanted = new Map<string, Set<string>>();
    for (const a of refs) {
      for (const row of data.records) {
        const v = row.__overrides?.[a.api_slug]?.value ?? row[a.api_slug];
        if (v == null || v === '') continue;
        const set = wanted.get(a.references_object!) ?? new Set<string>();
        set.add(String(v));
        wanted.set(a.references_object!, set);
      }
    }
    if (!wanted.size) return;

    let cancelled = false;
    Promise.all(
      [...wanted].map(async ([target, values]) => {
        const p = new URLSearchParams({ lookup: '1', values: [...values].join(',') });
        const res = await fetch(`/api/fin/records/${target}?${p}`, { credentials: 'same-origin' });
        if (!res.ok) return [target, []] as const;
        const body = await res.json();
        return [target, body.options ?? []] as const;
      })
    ).then((pairs) => {
      // Merged, not replaced: labels resolved on an earlier page stay valid,
      // so paging back does not flash raw keys.
      if (!cancelled) setLookups((prev) => {
        const next = { ...prev };
        for (const [target, opts] of pairs) {
          const byValue = new Map((next[target] ?? []).map((o) => [o.value, o]));
          for (const o of opts) byValue.set(o.value, o);
          next[target] = [...byValue.values()];
        }
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [data]);
  useEffect(() => {
    setOffset(0); setSearch(''); setDebounced(''); setSort(null);
    setVisibility({}); setSizing({}); setOrder([]);
  }, [object]);

  /**
   * Save one cell and splice the returned row back in place.
   * No refetch: that was the lag, and re-reading the page is what let an
   * edited row change position.
   */
  const save = useCallback(
    async (key: string, slug: string, value: string | null) => {
      const res = await fetch(`/api/fin/records/${object}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ key, values: { [slug]: value } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body.error ?? 'Could not save', {
          description: body.rejected?.join(', ') ?? body.hint,
        });
        throw new Error(body.error ?? 'save failed');
      }
      if (body.record) {
        setData((d) => d && ({
          ...d,
          records: d.records.map((r) => (r.__key === key ? (body.record as RecordRow) : r)),
        }));
      }
      toast.success('Saved');
    },
    [object]
  );

  const attributes = data?.attributes ?? [];
  const sourceSystem = data?.source_system ?? null;
  const mode = data?.object.mode ?? 'readonly';

  const columns = useMemo<ColumnDef<RecordRow>[]>(
    () => attributes.map((a) => ({
      id: a.api_slug,
      accessorKey: a.api_slug,
      header: a.label,
      size: a.type === 'currency' || a.type === 'number' ? 120
        : a.type === 'date' ? 110
        : a.api_slug.includes('description') || a.api_slug.includes('note') ? 260
        : 160,
      minSize: 70,
      cell: ({ row }) => (
        <EditableCell
          attr={a}
          sourceValue={row.original[a.api_slug]}
          override={row.original.__overrides?.[a.api_slug]}
          sourceSystem={sourceSystem}
          editable={a.ownership === 'user' && mode !== 'readonly'}
          lookup={a.references_object ? lookups[a.references_object] : undefined}
          onCommit={(v) => save(row.original.__key, a.api_slug, v)}
        />
      ),
    })),
    [attributes, sourceSystem, mode, save, lookups]
  );

  const table = useReactTable({
    data: data?.records ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (r) => r.__key,
    state: { columnVisibility: visibility, columnSizing: sizing, columnOrder: order },
    onColumnVisibilityChange: setVisibility,
    onColumnSizingChange: setSizing,
    onColumnOrderChange: setOrder,
    columnResizeMode: 'onChange',
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
  });

  if (loading && !data) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-8 w-64" />
        {Array.from({ length: 10 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load}>Try again</Button>
      </div>
    );
  }
  if (!data) return null;

  const { object: obj } = data;
  const editableCount = attributes.filter((a) => a.ownership === 'user').length;
  const page = Math.floor(offset / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(data.total / PAGE));
  const hiddenCount = Object.values(visibility).filter((v) => v === false).length;

  const toggleSort = (id: string) => {
    setOffset(0);
    setSort((s) => (!s || s.id !== id ? { id, desc: false } : s.desc ? null : { id, desc: true }));
  };

  /**
   * Move a column to where it was dropped.
   *
   * The first column is frozen, so this is also how you choose what stays on
   * screen while the rest scrolls — the freeze follows position rather than
   * being pinned to one attribute.
   */
  const moveColumn = (from: string, to: string) => {
    if (from === to) return;
    const current = order.length ? [...order] : attributes.map((a) => a.api_slug);
    const fromAt = current.indexOf(from);
    const toAt = current.indexOf(to);
    if (fromAt < 0 || toAt < 0) return;
    current.splice(toAt, 0, current.splice(fromAt, 1)[0]);
    setOrder(current);
  };

  return (
    <div className="flex h-full flex-col">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold">{obj.label}</h2>
          <Badge variant="secondary" className="text-[10px] tabular-nums">
            {data.total.toLocaleString()}
          </Badge>
        </div>

        {obj.mode === 'overlay' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="cursor-default gap-1 text-[10px]">
                <Lock className="h-2.5 w-2.5" />
                {sourceSystem ?? 'Warehouse'}-owned
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Values come from {sourceSystem ?? 'the warehouse'} and cannot be edited — the next
              import would overwrite them. {editableCount} classification fields are yours, stored
              separately so they survive each reload.
            </TooltipContent>
          </Tooltip>
        )}
        {obj.mode === 'readonly' && (
          <Badge variant="outline" className="gap-1 text-[10px]">
            <Sparkles className="h-2.5 w-2.5" /> Derived
          </Badge>
        )}

        <FilterBar
          attributes={attributes}
          operators={data.filter_operators ?? []}
          value={filters}
          onChange={(next) => { setFilters(next); setOffset(0); }}
        />

        <div className="ml-auto flex items-center gap-2">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search all ${data.total.toLocaleString()}…`}
              className="h-8 pl-8 pr-7 text-xs"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                Columns
                {hiddenCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[9px]">
                    {hiddenCount} hidden
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-auto">
              <DropdownMenuLabel className="text-xs">Show columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table.getAllLeafColumns().map((col) => {
                const attr = attributes.find((a) => a.api_slug === col.id);
                return (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={col.getIsVisible()}
                    onCheckedChange={(v) => col.toggleVisibility(!!v)}
                    onSelect={(e) => e.preventDefault()}
                    className="text-xs"
                  >
                    <span className="flex-1">{attr?.label ?? col.id}</span>
                    {attr?.ownership === 'user' && (
                      <span className="ml-2 h-1 w-1 rounded-full bg-primary" />
                    )}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {obj.mode === 'editable' ? (
            <Button size="sm" className="h-8"
              onClick={() => { setSheetRecord(null); setSheetOpen(true); }}>
              <Plus className="mr-1 h-3.5 w-3.5" /> New
            </Button>
          ) : (
            // Shown disabled rather than hidden: a button that silently is not
            // there reads as a broken one, and the reason is worth stating.
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button size="sm" className="h-8 pointer-events-none" disabled>
                    <Plus className="mr-1 h-3.5 w-3.5" /> New
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Rows here come from {sourceSystem ?? 'the warehouse'} and are reloaded by each
                import, so a row created in the app would disappear. Add it at the source instead
                — the classification columns stay yours.
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto">
        <Table className="text-xs" style={{ width: table.getTotalSize() + 32 }}>
          <TableHeader className="sticky top-0 z-20 bg-background">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {hg.headers.map((h, i) => {
                  const attr = attributes.find((a) => a.api_slug === h.column.id);
                  const active = sort?.id === h.column.id;
                  return (
                    <TableHead
                      key={h.id}
                      style={{ width: h.getSize() }}
                      onDragOver={(e) => {
                        const from = dragRef.current;
                        if (!from || from === h.column.id) return;
                        e.preventDefault();
                        setDropTarget(h.column.id);
                      }}
                      onDragLeave={() => setDropTarget((t) => (t === h.column.id ? null : t))}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = e.dataTransfer.getData('text/plain') || dragRef.current;
                        if (from) moveColumn(from, h.column.id);
                        dragRef.current = null;
                        setDragging(null); setDropTarget(null);
                      }}
                      className={cn(
                        'relative select-none whitespace-nowrap',
                        // Index-based, so the freeze follows whatever was dragged
                        // into first place rather than one named attribute.
                        i === 0 && 'sticky left-0 z-30 border-r bg-background',
                        dragging === h.column.id && 'opacity-40',
                        dropTarget === h.column.id && 'bg-primary/10 outline outline-1 outline-primary'
                      )}
                    >
                      <button
                        draggable
                        onDragStart={(e) => {
                          dragRef.current = h.column.id;
                          setDragging(h.column.id);
                          e.dataTransfer.effectAllowed = 'move';
                          // Firefox refuses to start a drag without payload.
                          e.dataTransfer.setData('text/plain', h.column.id);
                        }}
                        onDragEnd={() => {
                          dragRef.current = null;
                          setDragging(null); setDropTarget(null);
                        }}
                        onClick={() => toggleSort(h.column.id)}
                        title="Drag to reorder · click to sort"
                        className={cn(
                          'group inline-flex cursor-grab items-center gap-1 rounded-sm px-0.5 hover:text-foreground active:cursor-grabbing',
                          attr?.ownership !== 'user' && !active && 'text-muted-foreground'
                        )}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {attr?.ownership === 'user' && (
                          <span className="h-1 w-1 rounded-full bg-primary" />
                        )}
                        {active ? (
                          sort!.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ChevronsUpDown className="h-3 w-3 opacity-0 group-hover:opacity-40" />
                        )}
                      </button>
                      {/* Drag to resize */}
                      <div
                        onMouseDown={h.getResizeHandler()}
                        onTouchStart={h.getResizeHandler()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none',
                          'hover:bg-primary/40',
                          h.column.getIsResizing() && 'bg-primary'
                        )}
                      />
                    </TableHead>
                  );
                })}
                <TableHead className="w-8" />
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={table.getVisibleLeafColumns().length + 1}
                  className="h-24 text-center text-muted-foreground">
                  {debounced ? `Nothing matches “${debounced}”.` : 'No records.'}
                </TableCell>
              </TableRow>
            )}
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="group">
                {row.getVisibleCells().map((cell, i) => (
                  <TableCell
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={cn(
                      'overflow-hidden p-0',
                      i === 0 && 'sticky left-0 z-10 border-r bg-background group-hover:bg-muted/50'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
                <TableCell className="w-8 p-0">
                  <button
                    type="button"
                    aria-label="Open record"
                    onClick={() => { setSheetRecord(row.original); setSheetOpen(true); }}
                    className="flex h-7 w-8 items-center justify-center text-muted-foreground/0 hover:bg-accent focus-visible:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 group-hover:text-muted-foreground"
                  >
                    <PanelRight className="h-3.5 w-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <RecordSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        object={obj.slug}
        objectLabel={obj.label}
        mode={obj.mode}
        sourceSystem={sourceSystem}
        attributes={attributes}
        record={sheetRecord}
        lookups={lookups}
        onSaved={load}
      />

      {/* ── Pager ── */}
      <div className="flex items-center justify-between gap-3 border-t px-4 py-2 text-xs text-muted-foreground">
        <span className="tabular-nums">
          {data.total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE, data.total)}`} of{' '}
          {data.total.toLocaleString()}
          {debounced && ' matching'}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 px-2"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="px-2 tabular-nums">{page} / {pages}</span>
          <Button variant="outline" size="sm" className="h-7 px-2"
            disabled={offset + PAGE >= data.total || loading}
            onClick={() => setOffset(offset + PAGE)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
